import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTextDomainContext } from "@/app/providers";
import { useUpdateUser } from "@/hooks";
import {
  Box,
  Button,
  Drawer,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { PasswordInput } from "@/components";

interface ChangePasswordRow {
  id?: string;
  name?: string;
  email?: string;
  [key: string]: any;
}

interface ChangePasswordProps {
  open?: boolean;
  handleClose?: () => void;
  row?: ChangePasswordRow;
  parentPath?: string;
}

export const ChangePassword = (
  props: ChangePasswordProps,
): React.ReactElement => {
  //region Loading data
  const { enqueueSnackbar } = useSnackbar();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const initialValue = useMemo(() => {
    return {
      userId: "",
      passwordShown: false,
      confirmPasswordShown: false,
      inputName: props.row.name,
      inputEmail: props.row.email,
      inputPassword: "",
      inputConfirmPassword: "",
    };
  }, [props.row]);
  //endregion Loading data

  //region React Hook (useState)
  // const [userId, setUserId] = useState(initialValue.userId);
  // const [passwordShown, setPasswordShown] = useState(
  //   initialValue.passwordShown,
  // );
  // const [confirmPasswordShown, setConfirmPasswordShown] = useState(
  //   initialValue.confirmPasswordShown,
  // );
  // const [inputName, setInputName] = useState(initialValue.inputName);
  // const [inputEmail, setInputEmail] = useState(initialValue.inputEmail);
  const [inputPassword, setInputPassword] = useState(
    initialValue.inputPassword,
  );
  const [inputConfirmPassword, setInputConfirmPassword] = useState(
    initialValue.inputConfirmPassword,
  );
  //endregion React Hook (useState)

  //#region useMemo
  const inputName = useMemo(() => {
    return props.row.name || "";
  }, [props.row.name]);

  const inputEmail = useMemo(() => {
    return props.row.email || "";
  }, [props.row.email]);
  //#endregion useMemo

  //#region Handler
  const handleClose = () => {
    resetForm();
    props.handleClose();
  };
  //#endregion Handler

  //region React Hook (useEffect)
  // useEffect(() => {
  //   if (props?.row?.id) {
  //     // setUserId(props.row.id);
  //     setInputName(props.row.name);
  //     setInputEmail(props.row.email);
  //   }
  // }, [props.row]);

  //endregion React Hook (useEffect)

  //region Handlers
  const resetForm = useCallback(() => {
    // setUserId(initialValue.userId);
    // setInputName(initialValue.inputName);
    // setInputEmail(initialValue.inputEmail);
    setInputPassword(initialValue.inputPassword);
    setInputConfirmPassword(initialValue.inputConfirmPassword);
  }, [initialValue]);

  const [emailError, setEmailError] = useState(false);

  const { updateUser, loading: updating } = useUpdateUser(
    () => {
      resetForm();
      handleClose();
      enqueueSnackbar(gettext("The password has been updated."), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(`Backend Error: ${error}`, {
          variant: "error",
        });
      }
    },
  );

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (props.row.id) {
      const variables = {
        userId: props.row.id,
        password: inputPassword,
        confirmPassword: inputConfirmPassword,
      };

      updateUser({
        variables: variables,
        refetchQueries: [
          "AllUsers",
          "SystemUsers",
          "PartnerUsers",
          "CompanyUsers",
        ],
      }).then();
    }
  };
  // const togglePassword = () => {
  //   setPasswordShown(!passwordShown);
  // };

  // const toggleConfirmPassword = () => {
  //   setConfirmPasswordShown(!confirmPasswordShown);
  // };

  // const handleNameChange = (value) => {
  //   setInputName(value);
  // };
  //endregion Handlers

  return (
    <Drawer
      anchor={"right"}
      open={props.open}
      onClose={handleClose}
      sx={{
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
      slotProps={{
        paper: {
          sx: {
            padding: 4,
            maxWidth: 350,
          },
        },
      }}
    >
      <Box
        sx={{
          width: "auto",
        }}
      >
        <form onSubmit={submitHandler}>
          <Stack
            spacing={4}
            sx={{
              height: "100%",
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
              {!props.row.id && gettext("User ID is missing")}
              {props.row.id && gettext("Change Password")}
            </Typography>

            <Box
              sx={{
                height: "100%",
              }}
            >
              <Grid
                container
                spacing={4}
                sx={{
                  height: "100%",
                }}
              >
                <Grid size={{ xs: 12 }}>
                  <TextField
                    type="text"
                    label={gettext("Name")}
                    placeholder={gettext("Enter name")}
                    size="small"
                    required
                    fullWidth
                    disabled
                    value={inputName}
                    // onChange={(e) => handleNameChange(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    type="email"
                    label={gettext("Email")}
                    placeholder={gettext("Enter email")}
                    size="small"
                    required
                    fullWidth
                    disabled
                    autoComplete="off"
                    value={inputEmail}
                    // onChange={(e) => {
                    //   setInputEmail(e.target.value);
                    //   setEmailError(false);
                    // }}
                    error={emailError}
                    helperText={
                      emailError &&
                      gettext("The mail already exists in the system!")
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <PasswordInput
                    label={gettext("Password")}
                    placeholder={gettext("Enter password")}
                    size="small"
                    required
                    fullWidth
                    autoComplete="new-password"
                    value={inputPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setInputPassword(e.target.value)
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <PasswordInput
                    label={gettext("Confirm Password")}
                    placeholder={gettext("Enter password again")}
                    size="small"
                    required
                    fullWidth
                    autoComplete="new-password"
                    value={inputConfirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setInputConfirmPassword(e.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Paper elevation={0}>
              <Grid
                container
                spacing={4}
                sx={{
                  width: "100%",
                }}
              >
                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleClose()}
                  >
                    {gettext("Cancel")}
                  </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    loading={updating}
                    fullWidth
                    type="submit"
                  >
                    {gettext("Save")}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};
